import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Grid2,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Card } from '@liminal/web-ui/primitives/Card';
import { styled } from '@mui/material/styles';
import { Skeleton } from '@liminal/web-ui/feedback/Skeleton';
import { ErrorState } from '@liminal/web-ui/feedback/ErrorState';
import { Button } from '@liminal/web-ui/primitives/Button';
import { StatusPill } from '@liminal/web-ui/primitives/StatusPill';
import { CopyableId } from '@liminal/web-ui/display/CopyableId';
import { DetailRow } from '@liminal/web-ui/display/DetailRow';
import { useTransaction } from '@/hooks/useTransactions';
import { formatOrderDate } from '@liminal/utils/date';
import {
  txContextVariant,
  txStatusVariant,
  directionVariant,
  formatEnumLabel,
} from '@/components/ui/statusHelpers';
import { AccountIdLink } from '@/components/ui/AccountIdLink';
import { MonoCell } from '@/components/ui/MonoCell';

const SectionHeading = styled(Typography)({
  marginBottom: 16,
});

export function TransactionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: tx, isLoading, isError } = useTransaction(id ?? '');

  const entries = tx?.entries ?? [];
  const metadata = Object.entries(tx?.metadata ?? {});

  // Ramp transactions carry the provider's own transaction id in metadata
  // under the ambiguous key "transaction_id" (see ramps buildLedgerMetadata).
  // Surface it as a labelled first-class field so it isn't mistaken for the
  // ledger transaction id.
  const provider = tx?.metadata?.provider;
  const providerTxId = provider ? tx?.metadata?.transaction_id : undefined;
  const providerLabel = provider
    ? provider.charAt(0).toUpperCase() + provider.slice(1)
    : undefined;

  return (
    <Stack spacing={3}>
      <Box>
        <Button variant="link" size="sm" startIcon="arrow-back" onClick={() => navigate('/transactions')}>
          Back to Transactions
        </Button>
      </Box>

      {isLoading ? (
        <Card variant="elevated">
          <Skeleton width="40%" height={32} />
          <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} height={32} />
            ))}
          </Box>
        </Card>
      ) : isError || !tx ? (
        <Card variant="elevated">
          <ErrorState message="Failed to load this transaction. The request to the API did not succeed — this is not the same as the transaction not existing." />
        </Card>
      ) : (
        <>
          <Card variant="elevated">
              <SectionHeading variant="h6">Transaction</SectionHeading>
              <Grid2 container spacing={2}>
                <Grid2 size={{ xs: 12, md: 6 }}>
                  <DetailRow layout="stacked" label="Transaction ID" value={tx.id ? <CopyableId value={tx.id} label="Copy transaction ID" /> : '—'} />
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6 }}>
                  <DetailRow layout="stacked" label="Request ID (Idempotency Key)" value={tx.requestId ? <CopyableId value={tx.requestId} label="Copy request ID" /> : '—'} />
                </Grid2>
                {providerTxId && (
                  <Grid2 size={{ xs: 12, md: 6 }}>
                    <DetailRow layout="stacked"
                      label={`${providerLabel} Transaction ID`}
                      value={<CopyableId value={providerTxId} label={`Copy ${providerLabel} transaction ID`} />}
                    />
                  </Grid2>
                )}
                <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                  <DetailRow layout="stacked" label="Context" value={<StatusPill
                        label={formatEnumLabel(tx.context)}
                        tone={txContextVariant(tx.context)}
                        size="sm"
                      />} />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                  <DetailRow layout="stacked" label="Status" value={<StatusPill
                        label={formatEnumLabel(tx.status)}
                        tone={txStatusVariant(tx.status)}
                        size="sm"
                      />} />
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6 }}>
                  <DetailRow layout="stacked" label="Note" value={tx.note ?? '—'} />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                  <DetailRow layout="stacked" label="Created At" value={(tx.createdAt ? formatOrderDate(tx.createdAt) : undefined) ?? '—'} />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                  <DetailRow layout="stacked" label="Updated At" value={(tx.updatedAt ? formatOrderDate(tx.updatedAt) : undefined) ?? '—'} />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                  <DetailRow layout="stacked" label="Confirmed At" value={(tx.confirmedAt ? formatOrderDate(tx.confirmedAt) : undefined) ?? '—'} />
                </Grid2>
              </Grid2>
          </Card>

          <Card variant="elevated">
              <SectionHeading variant="h6">Entries</SectionHeading>
              {entries.length > 0 ? (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Entry ID</TableCell>
                      <TableCell>Direction</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Asset</TableCell>
                      <TableCell>Account</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {entries.map((entry, idx) => (
                      <TableRow key={entry.id ?? idx} hover>
                        <MonoCell>{entry.id ?? '—'}</MonoCell>
                        <TableCell>
                          <StatusPill
                            label={formatEnumLabel(entry.direction)}
                            tone={directionVariant(entry.direction)}
                            size="sm"
                          />
                        </TableCell>
                        <TableCell>{entry.amount ?? '—'}</TableCell>
                        <TableCell>{entry.assetSymbol ?? '—'}</TableCell>
                        <TableCell>
                          <AccountIdLink accountId={entry.accountId} full />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography variant="body2" color="text.disabled">
                  This transaction has no entries.
                </Typography>
              )}
          </Card>

          <Card variant="elevated">
              <SectionHeading variant="h6">Metadata</SectionHeading>
              {metadata.length > 0 ? (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Key</TableCell>
                      <TableCell>Value</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {metadata.map(([key, value]) => (
                      <TableRow key={key} hover>
                        <TableCell>{key}</TableCell>
                        <MonoCell>
                          <CopyableId value={value} label={`Copy ${key}`} />
                        </MonoCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography variant="body2" color="text.disabled">
                  No metadata on this transaction.
                </Typography>
              )}
          </Card>
        </>
      )}
    </Stack>
  );
}
